/**
 * Shared client-wide report catalog for index cards, sidebar nav, package picker,
 * the shared filter policy, and the "How reports work" explainer.
 * Package entries may differ slightly (e.g. Response time splits into two package reports).
 */

function byTitle(a, b) {
  return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
}

/**
 * Filter policy: `patients` (current account status) and `billing` (billing-approved)
 * are hidden and ignored on reports whose cohort comes from as-of-period rules or
 * dated activity, so a past period gives the same answer whenever it is run.
 */
const PERIOD_STABLE_FILTERS = { patients: false, billing: false };

/**
 * @typedef {{ shows: string, includes: string, counting: string[], limits?: string }} ReportExplainer
 * @typedef {{ id: string, title: string, to: string, description: string,
 *   packageEntries?: { id: string, title: string }[], filters?: { patients: boolean, billing: boolean },
 *   explainer: ReportExplainer }} ClientWideReport
 */

/** @type {ClientWideReport[]} */
const CLIENT_WIDE_REPORTS = [
  {
    id: 'billing',
    title: 'Billing report',
    to: '/reports/billing',
    description:
      'Per-patient billing rollup: readings counts, care minutes, provider, diagnosis, and insurance for the period.',
    explainer: {
      shows: 'One row per patient with readings, care minutes, provider, primary condition, insurance, and billing status for one month.',
      includes:
        'By default, patients with an Active RPM status and an active account on the last day of the month (the Cohort selector on the page can change this). The Billing filter can limit it to patients marked Bill.',
      counting: [
        'Once billing for the month is finalized, the report shows the frozen billing snapshot taken at finalization.',
        'Provider and primary condition are the ones in effect on the last day of the month.',
      ],
      limits:
        'Before finalization the figures are live and can change (e.g. if an encounter is voided). Reopening a billing period makes them live again until it is re-finalized.',
    },
  },
  {
    id: 'care-alerts',
    title: 'Care alerts',
    to: '/reports/care-alerts',
    description: 'Acknowledgement and resolution timelines and cohort metrics for alerts in scope.',
    packageEntries: [{ id: 'care-alerts', title: 'Care alerts' }],
    filters: PERIOD_STABLE_FILTERS,
    explainer: {
      shows: 'How many care alerts were created, how quickly they were acknowledged and resolved, and breakdowns by level, pathway, source, and team member.',
      includes: 'Alerts for patients with an Active RPM status (enrolled and not on hold) on at least one day of the period.',
      counting: [
        'Created = alerts created during the period.',
        'Resolved < 24 Hours = alerts from that group that were resolved within 24 hours of their creation time.',
        'Acknowledgement times use alerts acknowledged during the period.',
        'Historical alerts (from old device readings uploaded late) are excluded by default.',
      ],
    },
  },
  {
    id: 'care-time-by-patient',
    title: 'Care time by patient',
    to: '/reports/care-time/by-patient',
    description:
      'Per-patient rollup with sorting filters. Highlight patients below 20 minutes in the selected or adjacent month.',
    packageEntries: [{ id: 'care-time-by-patient', title: 'Care time by patient' }],
    filters: PERIOD_STABLE_FILTERS,
    explainer: {
      shows: 'Care minutes and encounters per patient for the period, compared with the previous block of the same length.',
      includes: 'Every patient in scope who has care time recorded in the period, regardless of whether their account is active today.',
      counting: ['Minutes come from the monthly billing rollups (logged encounter time).'],
      limits: 'If an encounter is voided later, the live figure changes; frozen snapshots keep what was reported.',
    },
  },
  {
    id: 'care-time',
    title: 'Care time summary',
    to: '/reports/care-time',
    description: 'Total session minutes, encounter volumes, and type breakdown within a calendar period.',
    packageEntries: [{ id: 'care-time', title: 'Care time summary' }],
    filters: PERIOD_STABLE_FILTERS,
    explainer: {
      shows: 'Total care minutes, encounter counts, patients reaching the 20-minute mark, and minutes by encounter type.',
      includes: 'All care time logged during the period for patients in scope.',
      counting: ['Headline totals come from monthly billing rollups; minutes by type come from the encounters themselves.'],
      limits: 'If an encounter is voided later, the live figure changes; frozen snapshots keep what was reported.',
    },
  },
  {
    id: 'disease',
    title: 'Disease & demographics',
    to: '/reports/disease',
    description:
      'Cohort profile: condition prevalence, comorbidity, age, gender, and a ZIP-level patient distribution map.',
    packageEntries: [{ id: 'disease', title: 'Disease & demographics' }],
    filters: PERIOD_STABLE_FILTERS,
    explainer: {
      shows: 'A profile of the patient group: most common conditions, number of conditions per patient, age groups, gender, and where patients live by ZIP.',
      includes: 'Patients with an Active RPM status (enrolled and not on hold) on at least one day of the period.',
      counting: [
        'A condition counts if its diagnosis was in effect on any day of the period (diagnoses ended before the period, or starting after it, are not counted).',
        'Age is the patient\'s age on the last day of the period.',
      ],
      limits:
        'Gender, ZIP, and client come from the patient record. Live reports use the record as it is today; frozen snapshots keep the values at freeze time.',
    },
  },
  {
    id: 'kpi',
    title: 'KPI report',
    to: '/reports/kpi',
    description:
      'Monthly quality KPIs by client — device adherence, CPT 99457 / 99458 met, and alerts reviewed within 24 hours — against goal and stretch targets.',
    filters: PERIOD_STABLE_FILTERS,
    capability: 'canSeeKpiReport',
    explainer: {
      shows: 'For each month and client: the share of patients meeting each quality KPI, compared with its goal and stretch targets.',
      includes:
        'Patients with an active account and Active RPM (not on hold) for the whole month, enrolled on or before the 1st. Alert review counts every care alert created in the month for the client’s patients, except historical-reading alerts.',
      counting: [
        'Percentages are pooled (total patients meeting ÷ total patients), then rounded to the nearest whole percent; .5 rounds up.',
        'Finalized billing months use the frozen billing snapshot; open months use live data and update nightly.',
        'An alert counts as reviewed on time when it is acknowledged within 24 hours of creation, around the clock.',
      ],
      limits: 'The current month shows month-to-date values. A KPI with nothing to measure in a month shows “—”.',
    },
  },
  {
    id: 'messages',
    title: 'Messages',
    to: '/reports/messages',
    description:
      'Patient vs. care-team message volume by day, plus acknowledgement response times. Client-branded and PDF-ready.',
    packageEntries: [{ id: 'messages', title: 'Messages' }],
    filters: PERIOD_STABLE_FILTERS,
    explainer: {
      shows: 'Messages from patients and the care team by day, and how quickly patient messages were acknowledged.',
      includes: 'Messages sent during the period by or to patients in scope. Automated and broadcast messages are excluded from care-team counts.',
      counting: [
        '"Awaiting acknowledgement" means not yet read by the end of the period. Reading an old message later does not change a past month.',
      ],
    },
  },
  {
    id: 'patient-listing',
    title: 'Patient Listing',
    to: '/reports/patient-listing',
    description:
      'Roster of patients in an active RPM session as of a chosen date, with New vs Current labeling and links to each chart.',
    packageEntries: [{ id: 'patient-listing', title: 'Patient Listing' }],
    filters: PERIOD_STABLE_FILTERS,
    explainer: {
      shows: 'A list of patients with an Active RPM status on a chosen date, with patients enrolled that month labeled New.',
      includes: 'Patients enrolled in RPM and not on hold on the as-of date. In the client package the as-of date is the last day of the period.',
      counting: ['Status on the as-of date is rebuilt from enrollment, discharge, and hold history.'],
      limits: 'Names, client, location, and care manager come from the patient record (as of today live, or at freeze time in a snapshot).',
    },
  },
  {
    id: 'pro-summary',
    title: 'Patient Reported Outcomes',
    to: '/reports/pro-summary',
    description:
      'Patient survey results: overall score, answers by question, % agree, participation, and quarterly trend by client. No patient names or comments.',
    packageEntries: [{ id: 'pro-summary', title: 'Patient Reported Outcomes' }],
    filters: PERIOD_STABLE_FILTERS,
    explainer: {
      shows: 'Survey scores (5 = Strongly agree to 1 = Strongly disagree) for the selected month and the 3 months ending with it, participation, and % agree by quarter.',
      includes:
        'Surveys from pathways marked "Report as survey". Each month about one third of eligible patients (active account, Active RPM for at least 2 months) receive the survey; unanswered surveys expire after 30 days.',
      counting: [
        'Scores count in the month the survey was completed. Participation counts in the month it was sent.',
        '% Agree is the share of answers that were 4 or 5.',
        'Any group with fewer than 5 responses has its scores hidden.',
      ],
      limits: 'Comments and patient names are never shown here; they are in the internal PRO Detail report. Patient scope filters do not apply.',
    },
  },
  {
    id: 'response-time',
    title: 'Response time',
    to: '/reports/response-time',
    description:
      'Message acknowledgement and care-alert ack/resolution times, broken down by day and by 4-hour time-of-day window.',
    packageEntries: [
      { id: 'response-time-alerts', title: 'Response time — care alerts' },
      { id: 'response-time-messages', title: 'Response time — messages' },
    ],
    filters: PERIOD_STABLE_FILTERS,
    explainer: {
      shows: 'Median times to acknowledge patient messages and to acknowledge and resolve care alerts, by day and by time of day.',
      includes: 'Messages and care alerts received during the period for patients in scope.',
      counting: ['Only acknowledgements and resolutions made by the last day of the period count, so later activity does not change a past month.'],
    },
  },
  {
    id: 'vitals',
    title: 'Vitals report',
    to: '/reports/vitals',
    description: 'Flat export of vital readings by parameter — client, patient, date, source, and value.',
    explainer: {
      shows: 'A flat list of vital readings (date, source, value) for the period.',
      includes: 'Readings received during the period for patients in scope. All filters apply.',
      counting: ['Quarantined readings are excluded.'],
      limits: 'This is an operational export and is not frozen.',
    },
  },
  {
    id: 'volume-activity',
    title: 'Volume Activity Detail',
    to: '/reports/volume-activity',
    description:
      'RPM active census opening and ending, plus enrollments, holds, returns, discharges, net change, by-client rollups, patient-level activity detail, and named patient rosters for opening, added, and removed.',
    packageEntries: [{ id: 'volume-activity', title: 'Volume Activity Detail' }],
    filters: PERIOD_STABLE_FILTERS,
    explainer: {
      shows: 'How many patients had an Active RPM status at the start and end of the period, who was added or removed, and every enrollment, hold, return, and discharge in between.',
      includes: 'Patients with RPM program history in scope; the census is rebuilt for the first and last day of the period.',
      counting: [
        'Opening = Active RPM at the start of the first day. Ending = Active RPM at the end of the last day.',
        'Added and Removed are net per patient: someone who leaves and comes back within the period is in neither.',
      ],
    },
  },
  {
    id: 'volume-activity-summary',
    title: 'Volume Activity Summary',
    to: '/reports/volume-activity-summary',
    description:
      'RPM active census opening and ending, plus enrollments, holds, returns, discharges, net change, and by-client rollups — without patient-level activity detail.',
    packageEntries: [{ id: 'volume-activity-summary', title: 'Volume Activity Summary' }],
    filters: PERIOD_STABLE_FILTERS,
    explainer: {
      shows: 'The same census figures as Volume Activity Detail (opening, added, removed, ending, net change, by client) without the patient-level lists.',
      includes: 'Same as Volume Activity Detail.',
      counting: ['Same as Volume Activity Detail.'],
    },
  },
];

export const CLIENT_WIDE_REPORTS_ALL = CLIENT_WIDE_REPORTS;

/** Index cards and sidebar links, sorted by title. */
export const CLIENT_WIDE_NAV = [...CLIENT_WIDE_REPORTS].sort(byTitle);

/** Catalog reports a capability set may open (entries with `capability` need that flag). */
export function clientWideNavFor(permissions) {
  return CLIENT_WIDE_NAV.filter((r) => !r.capability || permissions?.[r.capability] === true);
}

/** Package-selectable reports, sorted by title. */
export const PACKAGE_REPORTS = CLIENT_WIDE_REPORTS.flatMap((r) => r.packageEntries || []).sort(byTitle);

export const PACKAGE_REPORT_IDS = PACKAGE_REPORTS.map((r) => r.id);

/** Default standard package when a tenant has not configured one (Tools > Reports). */
export const DEFAULT_STANDARD_PACKAGE_IDS = ['volume-activity-summary', 'care-alerts', 'disease'];

/** Explainer page anchor for a catalog report id. */
export function explainerAnchor(reportId) {
  return `/reports/how-reports-work#report-${reportId}`;
}

const DEFAULT_FILTERS = { patients: true, billing: true };

/** Routes outside the catalog that also use period-stable filters. */
const EXTRA_ROUTE_FILTERS = new Map([
  ['/reports/package', PERIOD_STABLE_FILTERS],
  ['/reports/pro-detail', PERIOD_STABLE_FILTERS],
]);

/** Internal-only reports (never in the client package). */
export const INTERNAL_REPORTS = [
  {
    id: 'pro-detail',
    title: 'PRO Detail (internal)',
    to: '/reports/pro-detail',
    description:
      'Survey comments with patient name, client and date submitted, answer drill-down, low-score list, and audited CSV export. For internal use only.',
    capability: 'canSeeProInternalReport',
  },
];

/** Internal reports a capability set may open. */
export function internalReportsFor(permissions) {
  return INTERNAL_REPORTS.filter((r) => permissions?.[r.capability] === true);
}

/** Filter policy for a Reports route (pathname). */
export function filterPolicyForPath(pathname) {
  const path = String(pathname || '').replace(/\/+$/, '');
  if (EXTRA_ROUTE_FILTERS.has(path)) return EXTRA_ROUTE_FILTERS.get(path);
  const report = CLIENT_WIDE_REPORTS.find((r) => r.to === path);
  return report?.filters || DEFAULT_FILTERS;
}
